import { GroupEntity, UserEntity } from '@backstage/catalog-model';
import { EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  CircularProgress,
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import React from 'react';

import { InfoCard, Progress, Link } from '@backstage/core-components';
import { RequestUpdateButton } from '../../styles/Buttons';
import { MemberComponent, emailsOf } from '../../cards/MembersListCard';
import { GroupMailLink } from '../../GroupMail';
import { TeamsGroupChatLink } from '../../TeamsChat';
import {
  EntraGroupOut,
  EntraGroupsIn,
  useCurrentProfile,
  useUsersFromGroupMembers,
} from '../../../hooks';
import {
  EditGroupMembershipsDialog,
  GroupMembershipTarget,
} from './EditGroupMembershipsDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // The Entra group name sits beside the headline, where it is found at a
    // glance, but it stays in the background: it is an identifier one copies
    // over to Entra now and then, not what the card is about. Hence small,
    // muted and unbolded against the title it trails, and set as code - it is
    // a string to be read character by character, not prose.
    groupName: {
      marginLeft: theme.spacing(1.5),
      fontFamily: 'monospace',
      fontSize: '0.7em',
      fontWeight: 'normal',
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
    // Between the DP group and its CAuth one, which would otherwise read as
    // one long identifier.
    groupSeparator: {
      marginLeft: theme.spacing(1.5),
      fontSize: '0.7em',
      fontWeight: 'normal',
      color: theme.palette.text.disabled,
    },
  }),
);

/**
 * Whose permissions a group grants, in the two forms the lines below read it:
 * what the object owns, as the clause reads after "everything", and the object
 * itself.
 *
 * This is the whole of what the three pages disagree on, see
 * {@link groupDescriptions}.
 */
const GROUP_SUBJECTS: Record<
  GroupMembershipTarget['kind'],
  { owns: string; itself: string }
> = {
  team: { owns: 'the team owns', itself: 'the team' },
  department: {
    // A department owns nothing of its own; everything it has is what the
    // teams below it hold.
    owns: 'the departments and teams below own',
    itself: 'the department',
  },
  // A product has no technical group - an editor and a viewer of a product
  // would be the editor and the viewer of the team that owns it - so `owns` is
  // never read for one. It is stated all the same, so that the table is a
  // table and a technical group the CMDB hands a product later has a line.
  product: { owns: 'the product owns', itself: 'the product' },
};

/**
 * What each group of the object is good for, a line each, after "Introduction
 * | IAM AD/Entra resources" - the page every group card links to as Docs.
 *
 * The team, the department and the product page say the same thing about the
 * same two technical roles and about a group the object asked for itself, and
 * differ in whose things the permission is over and in nothing else. So the
 * sentences are written once here and the pages state none of them; what a
 * page does say, it says through its `target`.
 *
 * The CMDB names a technical group by its role, and editor and viewer are the
 * two that exist - the contributor the docs page also describes is not handed
 * out.
 */
function groupDescriptions(kind: GroupMembershipTarget['kind']) {
  const { owns, itself } = GROUP_SUBJECTS[kind];
  // Until the migration is through, part of the access still hangs off the
  // CAuth groups, so the two technical ones are not the whole story yet.
  const migration = (
    <>
      <br />
      <br />
      Attention: Some CAuth Entra groups are still required, we are currently
      migrating from CAuth groups to DP groups (see{' '}
      <Link to="https://rtltech.atlassian.net/browse/DPF-189">DPF-189</Link>).
    </>
  );
  return {
    technical: {
      editor: (
        <>
          {`Admin-like permissions to almost everything ${owns}: Cloud, On-Premises, GitLab, Kubernetes, Vault, Wiz.`}
          {migration}
        </>
      ),
      viewer: (
        <>
          {`Read permissions to everything ${owns}: Cloud, On-Premises, GitLab, Kubernetes, Wiz. Everyone ${itself} names as a contact is in it.`}
          {migration}
        </>
      ),
    } as Record<string, React.ReactNode>,
    custom: `Permissions for self-managed resources of ${itself}, e.g. a web UI or dashboard.`,
  };
}

/**
 * The order the technical groups are shown in, editor above viewer: the wider
 * permission first, and the same on every page.
 *
 * The CMDB states the pair in whichever order the file it was read from
 * happens to have - editor first for a team, viewer first for a department -
 * and that is no statement about either, so the page does not pass it on.
 */
const TECHNICAL_ORDER = ['editor', 'viewer'];

function byName(a: EntraGroupOut, b: EntraGroupOut) {
  return a.name.localeCompare(b.name);
}

function byTechnicalOrder(a: EntraGroupOut, b: EntraGroupOut) {
  // A technical group the CMDB adds later sorts behind the two known ones
  // rather than disappearing off the page.
  const rank = (group: EntraGroupOut) => {
    const index = TECHNICAL_ORDER.indexOf(group.name);
    return index < 0 ? TECHNICAL_ORDER.length : index;
  };
  return rank(a) - rank(b) || byName(a, b);
}

/**
 * The Entra groups of a team, of a department or of a product, a card each:
 * the whole of the Groups tab of any of the three pages.
 *
 * All three render this and nothing else of their own, so the tabs are the
 * same page down to the order of the cards. They differ in whose groups these
 * are and in nothing else - which the `target` already says, so it is all a
 * page hands over; even the line that tells what a group is good for is read
 * off it, see {@link groupDescriptions}.
 */
export const EntraGroupsGrid = (props: {
  groups?: EntraGroupsIn;
  target: GroupMembershipTarget;
}) => {
  const { groups, target } = props;
  const { technical: descriptions, custom: customDescription } =
    groupDescriptions(target.kind);
  const { loading, entities } = useUsersFromGroupMembers(groups);
  // Every team and every department has both categories, so an object without
  // them is one whose entity predates them rather than one still loading -
  // which is why the wait is told by `loading` and not by what came back.
  const { technical, custom } = entities ?? { technical: [], custom: [] };

  return (
    <Grid container spacing={3} alignItems="stretch">
      {loading ? (
        <Box m={4} width={1}>
          <Grid container justifyContent="center">
            <CircularProgress />
          </Grid>
        </Box>
      ) : (
        <>
          {[...technical].sort(byTechnicalOrder).map(group => (
            <EntraGroupCard
              key={group.name}
              group={group}
              cmdbGroupType="technical"
              target={target}
              description={descriptions[group.name]}
              loading={loading}
            />
          ))}
          {/* Custom groups have no order of their own, so they read as a list. */}
          {[...custom].sort(byName).map(group => (
            <EntraGroupCard
              key={group.name}
              group={group}
              cmdbGroupType="custom"
              target={target}
              description={customDescription}
              loading={loading}
            />
          ))}
        </>
      )}
    </Grid>
  );
};

/**
 * One Entra group, who is in it and the dialog that asks for that to change.
 *
 * The team, the department and the product page show the same card: since v13
 * the CMDB states the groups of all three the same way, down to the viewer
 * group of each being derived from the contacts of the object. Only whose
 * group it is differs, so everything else lives here - the split between the
 * declared members and the derived ones above all, which every page has to get
 * right for the dialog to write back the right thing.
 */
export const EntraGroupCard = (props: {
  group: EntraGroupOut;
  cmdbGroupType: 'technical' | 'custom';
  target: GroupMembershipTarget;
  /** What the group is good for - see {@link groupDescriptions}. */
  description?: React.ReactNode;
  loading: boolean;
}) => {
  const { group, cmdbGroupType, target, description, loading } = props;
  const { identity } = useCurrentProfile();
  const [openDialog, setOpenDialog] = React.useState(false);
  const toggleDialog = () => setOpenDialog(open => !open);

  // Everyone in the group that nobody wrote down: the CMDB puts the contacts
  // of the team or the department into its viewer group and derives them
  // afresh on every build.
  //
  // An entity that predates `declaredMembers` states none, and that is not the
  // same as a group nobody was written into. Read as the latter it makes the
  // whole group derived - every row locked, nothing editable - and the dialog
  // would then write an empty member list back, filing a merge request that
  // empties the group in the CMDB. So the card offers no edit at all until the
  // entity can say who was declared; it still shows who is in the group, which
  // is the part that is not in doubt.
  const { declaredMembers } = group;
  const declared = new Set((declaredMembers ?? []).map(it => it.metadata.name));
  const derived = group.members.filter(it => !declared.has(it.metadata.name));
  const displayName = group.full_name ?? group.name;

  return (
    <Grid item xs={12}>
      {identity && declaredMembers && (
        <EditGroupMembershipsDialog
          currentUserMail={identity.email!.toLowerCase()}
          groupFullName={displayName}
          target={target}
          cmdbGroupType={cmdbGroupType}
          cmdbGroupName={group.name}
          openDialog={openDialog}
          toggleDialog={toggleDialog}
          /*
           * Split in two: only the declared members are editable and only they
           * are written back, while the derived ones are shown locked so the
           * table is the group as it stands. Writing the derived ones back
           * would turn them into declared members that outlive the contact
           * they came from.
           */
          usersInGroup={declaredMembers}
          derivedUsersInGroup={derived}
        />
      )}
      <CMDBGroupMemberListCard
        memberDisplayTitle={
          cmdbGroupType === 'technical'
            ? `Group ${group.name.charAt(0).toUpperCase()}${group.name.slice(1)}s`
            : 'Custom Group Members'
        }
        displayName={displayName}
        shortName={group.name}
        legacyGroupName={group.cauth_full_name ?? undefined}
        description={description}
        loading={loading}
        members={group.members}
        action={identity && declaredMembers ? toggleDialog : undefined}
      />
    </Grid>
  );
};

/** @public */
export const CMDBGroupMemberListCard = (props: {
  members: UserEntity[];
  loading: boolean;
  memberDisplayTitle?: string;
  pageSize?: number;
  displayName?: string;
  /**
   * The name the CMDB gives the group within its team - `editor`, or what a
   * custom group was called - which a group chat with its members is named
   * by, next to the page.
   */
  shortName?: string;
  /**
   * The legacy `CAuth_…` group that stands for this one during the migration,
   * shown beside the DP group. Only a technical team group has one.
   */
  legacyGroupName?: string;
  /** What the group is good for, in a line - see {@link groupDescriptions}. */
  description?: React.ReactNode;
  /**
   * Opens the membership dialog. Left out for a group the portal cannot offer
   * to change, which drops the button rather than showing a dead one.
   */
  action?: () => void;
}) => {
  const {
    memberDisplayTitle = 'Members',
    pageSize = 50,
    members,
    loading,
    legacyGroupName,
    description,
    action,
  } = props;

  const classes = useStyles();
  const { entity: groupEntity } = useEntity<GroupEntity>();
  const {
    metadata: { name: groupName },
    spec: { profile },
  } = groupEntity;

  let displayName = '';
  if (props.displayName) {
    displayName = props.displayName;
  } else {
    displayName = profile?.displayName ?? groupName;
  }

  /*
   * A card handed a name of its own is about an AD group of the team, and the
   * msgraph provider names such an entity after its display name in lower
   * case, while the CMDB states it the way Entra spells it - so the ref is
   * lowered and the label keeps the spelling one searches Entra for. Without
   * a name the card is about the entity of the page, which is then what the
   * title links to.
   */
  const entraGroupRef = (entraName: string) => ({
    kind: 'Group',
    namespace: groupEntity.metadata.namespace ?? 'default',
    name: entraName.toLocaleLowerCase(),
  });
  const groupRef = props.displayName
    ? entraGroupRef(props.displayName)
    : groupEntity;

  const [page, setPage] = React.useState(1);
  const pageChange = (_: React.ChangeEvent<unknown>, pageIndex: number) => {
    setPage(pageIndex);
  };

  if (loading) {
    return <Progress />;
  }

  const nbPages = Math.ceil((members?.length || 0) / pageSize);
  const paginationLabel = nbPages < 2 ? '' : `, page ${page} of ${nbPages}`;

  // The chat is named after the page - a team, a department, a product - and
  // the short name of the group, e.g. `Team Acafo (editor)`, rather than the
  // Entra name, whose technical spelling says little in a chat list.
  const pageTitle = groupEntity.metadata.title ?? groupName;
  const chatName = props.shortName
    ? `${pageTitle} (${props.shortName})`
    : pageTitle;
  const actionButton = (
    <Box display="flex" alignItems="center">
      <TeamsGroupChatLink members={members ?? []} topicName={chatName} />
      <GroupMailLink addresses={emailsOf(members ?? [])} />
      {action && <RequestUpdateButton onClick={action} />}
    </Box>
  );

  const pagination = (
    <Pagination
      count={nbPages}
      page={page}
      onChange={pageChange}
      showFirstButton
      showLastButton
    />
  );

  return (
    <>
      <Grid item>
        <InfoCard
          title={
            <>
              {`${memberDisplayTitle} (${
                members?.length || 0
              }${paginationLabel})`}
              {/* Trailing the headline, see groupRef. */}
              <EntityRefLink
                className={classes.groupName}
                entityRef={groupRef}
                title={displayName}
              />
              {/* The msgraph provider takes in the CAuth groups as well, so
                  the legacy one links the same way. */}
              {legacyGroupName && (
                <>
                  <span className={classes.groupSeparator} aria-hidden>
                    /
                  </span>
                  <EntityRefLink
                    className={classes.groupName}
                    entityRef={entraGroupRef(legacyGroupName)}
                    title={legacyGroupName}
                  />
                </>
              )}
            </>
          }
          // One span: the subheader lays its children out as a flex row, so
          // loose text and links would each become a column of their own.
          subheader={
            <span>
              {description && <>{description} </>}(
              <Link to="https://rtltech.atlassian.net/wiki/spaces/DP/pages/1863989345/Introduction+IAM+AD+Entra+resources">
                Further Docs
              </Link>
              )
            </span>
          }
          {...(nbPages <= 1 ? {} : { actions: pagination })}
          action={actionButton}
        >
          <Grid container spacing={3}>
            {members && members.length > 0 ? (
              members
                .slice(pageSize * (page - 1), pageSize * page)
                .map(member => (
                  <MemberComponent member={member} key={member.metadata.uid} />
                ))
            ) : (
              <Box p={2}>
                <Typography>
                  This group has no {memberDisplayTitle.toLocaleLowerCase()}.
                </Typography>
              </Box>
            )}
          </Grid>
        </InfoCard>
      </Grid>
    </>
  );
};
